import * as React from 'react';
import { StepRunRouteComponentMatchParams, StepRunComponentProps } from '@/typescriptHelpers';
import { StepChecklistItems } from '../StepChecklistItems';
import Space from 'antd/lib/space';
import Layout from 'antd/lib/layout';
import { ChecklistItemPlacement, IntegrationCapability, ReleaseStepType, ChecklistItemStatusType } from '@/types';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import { StepRun } from '@/types/stepRun';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettings,
    selectAppStoreType,
    selectIntegrationForCapability,
    selectAppHasSelectCapabilitiesConfigured,
    selectJoinedCommsProvidersNamesList,
    selectWorkflowRunId,
    selectWorkflowId,
} from '@/selectors/selectors';
import { Link, useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import { createStepRunRoute } from '@/helpers/routeHelpers';
import { ArrowRightOutlined, CheckCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import Tooltip from 'antd/lib/tooltip';

interface StepChecklistItemsContainerProps {
    orgId: string;
    appId: string;
    releaseId: string;
    releaseStepType: ReleaseStepType;
    stepRuns: StepRun[];
}

const StepGroupChecklistContainer: React.FC<StepChecklistItemsContainerProps> = (
    props: StepChecklistItemsContainerProps,
) => {
    const { orgId, appId, releaseId, releaseStepType } = props;

    const platform = useSelector((state: RootState) => selectAppSettings(state, appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.Notifications),
    );
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const params = useParams<StepRunRouteComponentMatchParams>();

    const renderPingPendingButton = () => {
        const button = (
            <Button style={{ gap: 0, marginRight: 15 }} icon={<IntegrationIcon integrationId={commsIntegration?.id} />}>
                <span style={{ paddingLeft: 4 }}>Ping pending</span>
            </Button>
        );

        if (!hasCommsIntegrationConnected) {
            return (
                <Tooltip title={`Connect ${commsProviders} to ping owners about incomplete items`}>{button}</Tooltip>
            );
        }

        return button;
    };

    return (
        <Layout style={{ paddingBottom: 95 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '25px', marginTop: 24, marginBottom: 15 }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                            width: 'auto',
                        }}
                    >
                        <Space size={8}></Space>
                    </div>
                </div>
            </div>
            <div>
                {Array.from(props.stepRuns.entries()).map(([_, stepRun]) => {
                    const completedCount = stepRun.checklistItems.filter(
                        (item) => item.status?.status === ChecklistItemStatusType.Approved,
                    ).length;
                    const totalCount = stepRun.checklistItems.length;
                    const allCompleted = completedCount === totalCount;
                    const completedItemsString = `${completedCount} of ${totalCount}`;
                    const stepRunComponentProps: StepRunComponentProps = {
                        stepRunId: stepRun.id,
                        stepConfigId: stepRun.stepId,
                        workflowId: workflowId,
                        workflowRunId: workflowRunId,
                        orgId: orgId,
                        releaseId: releaseId,
                        appId: appId,
                    };
                    return (
                        <div style={{ marginBottom: 40 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Space>
                                    {stepRun.destinations &&
                                        stepRun.destinations.map((destination) => (
                                            <DestinationCounter destinations={[destination]} />
                                        ))}
                                </Space>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {displayTextForReleaseStepType(stepRun.type, platform, appStoreType, true)}
                                </Text>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {stepRun.displayName}
                                </Text>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <div>
                                    <Text
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 400,
                                            color: allCompleted ? runwayColors.statusGreen : runwayColors.statusYellow,
                                        }}
                                    >
                                        {completedItemsString}
                                    </Text>

                                    {allCompleted && (
                                        <CheckCircleFilled style={{ color: runwayColors.statusGreen, marginLeft: 4 }} />
                                    )}
                                </div>
                                <div style={{ marginLeft: 'auto' }}>
                                    {renderPingPendingButton()}
                                    <Button
                                        style={{ gap: 0, marginRight: 5 }}
                                        icon={
                                            <CheckCircleOutlined
                                                style={{ color: runwayColors.primary, marginRight: 5 }}
                                            />
                                        }
                                    >
                                        Mark all as completed...
                                    </Button>
                                    <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                    <Link
                                        to={createStepRunRoute(
                                            params.orgIdParam,
                                            params.appIdParam,
                                            params.releaseIdParam,
                                            stepRun.id,
                                        )}
                                        style={{ alignSelf: 'flex-end' }}
                                    >
                                        <Button
                                            type="link"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 200,
                                                padding: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'inline-flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    height: '100%',
                                                }}
                                            >
                                                <ArrowRightOutlined />
                                                View in Step
                                            </div>
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                            <Divider
                                type="horizontal"
                                style={{ marginTop: 13, marginBottom: 0, borderWidth: 2 }}
                            ></Divider>
                            <StepChecklistItems
                                orgId={orgId}
                                releaseId={releaseId}
                                appId={appId}
                                releaseStepType={releaseStepType}
                                items={stepRun.checklistItems}
                                isEditing={false}
                                onEditChecklistItem={null}
                                onAddComment={null}
                                placement={ChecklistItemPlacement.StepChecklist}
                                onUpdateOrder={null}
                                stepRunComponentProps={stepRunComponentProps}
                            />
                        </div>
                    );
                })}
            </div>
        </Layout>
    );
};

export default StepGroupChecklistContainer;
